import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button } from "reactstrap";
import { Link } from "react-router-dom";
import CommonSection from "../Shared/CommonSection";
import "../styles/my-cab-bookings.css";

const MyCabBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = () => {
    const savedBookings = JSON.parse(localStorage.getItem("cabBookings") || "[]");
    // Sort by most recent first
    savedBookings.sort((a, b) => new Date(b.bookedAt) - new Date(a.bookedAt));
    setBookings(savedBookings);
  };

  const cancelBooking = (bookingId) => {
    if (window.confirm("Are you sure you want to cancel this booking?")) {
      const updatedBookings = bookings.map(booking => 
        booking.id === bookingId 
          ? { ...booking, status: "cancelled" }
          : booking
      );
      localStorage.setItem("cabBookings", JSON.stringify(updatedBookings));
      setBookings(updatedBookings);
    }
  };

  const deleteBooking = (bookingId) => {
    if (window.confirm("Are you sure you want to delete this booking?")) {
      const updatedBookings = bookings.filter(booking => booking.id !== bookingId);
      localStorage.setItem("cabBookings", JSON.stringify(updatedBookings));
      setBookings(updatedBookings);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "confirmed":
        return "status__confirmed";
      case "cancelled":
        return "status__cancelled";
      case "completed":
        return "status__completed";
      default:
        return "";
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const filteredBookings = filter === "all" 
    ? bookings 
    : bookings.filter(b => b.status === filter);

  return (
    <>
      <CommonSection title="My Cab Bookings" />

      <section className="my__bookings__section">
        <Container>
          {/* Filter Tabs */}
          <div className="bookings__filter">
            <button 
              className={`filter__tab ${filter === "all" ? "active" : ""}`}
              onClick={() => setFilter("all")}
            >
              All Bookings ({bookings.length})
            </button>
            <button 
              className={`filter__tab ${filter === "confirmed" ? "active" : ""}`}
              onClick={() => setFilter("confirmed")}
            >
              Confirmed ({bookings.filter(b => b.status === "confirmed").length})
            </button>
            <button 
              className={`filter__tab ${filter === "completed" ? "active" : ""}`}
              onClick={() => setFilter("completed")}
            >
              Completed ({bookings.filter(b => b.status === "completed").length})
            </button>
            <button 
              className={`filter__tab ${filter === "cancelled" ? "active" : ""}`}
              onClick={() => setFilter("cancelled")}
            >
              Cancelled ({bookings.filter(b => b.status === "cancelled").length})
            </button>
          </div>

          {filteredBookings.length === 0 ? (
            <div className="no__bookings">
              <i className="ri-calendar-line"></i>
              <h3>No Bookings Found</h3>
              <p>
                {filter === "all" 
                  ? "You haven't made any cab bookings yet."
                  : `No ${filter} bookings found.`}
              </p>
              <Link to="/cabs">
                <Button className="book__now__btn">
                  <i className="ri-car-line"></i> Book a Cab Now
                </Button>
              </Link>
            </div>
          ) : (
            <Row>
              {filteredBookings.map((booking) => (
                <Col lg="6" md="12" key={booking.id}>
                  <div className="booking__card">
                    <div className="booking__card__header">
                      <div className="booking__id__section">
                        <span className="booking__label">Booking ID</span>
                        <span className="booking__id">{booking.id}</span>
                      </div>
                      <span className={`booking__status ${getStatusClass(booking.status)}`}>
                        {booking.status}
                      </span>
                    </div>

                    <div className="booking__card__body">
                      <div className="vehicle__info">
                        <div className="vehicle__icon">
                          <i className="ri-car-line"></i>
                        </div>
                        <div className="vehicle__details">
                          <h5>{booking.cab.name}</h5>
                          <span>{booking.cab.type}</span>
                        </div>
                      </div>

                      <div className="trip__details">
                        <div className="trip__route">
                          <div className="route__item">
                            <span className="route__icon pickup">
                              <i className="ri-map-pin-line"></i>
                            </span>
                            <div className="route__text">
                              <span className="route__label">Pickup</span>
                              <span className="route__value">{booking.trip.pickup}</span>
                            </div>
                          </div>
                          <div className="route__line"></div>
                          <div className="route__item">
                            <span className="route__icon drop">
                              <i className="ri-map-pin-2-line"></i>
                            </span>
                            <div className="route__text">
                              <span className="route__label">Drop</span>
                              <span className="route__value">{booking.trip.drop}</span>
                            </div>
                          </div>
                        </div>

                        <div className="trip__datetime">
                          <div className="datetime__item">
                            <i className="ri-calendar-line"></i>
                            <span>{booking.trip.date}</span>
                          </div>
                          <div className="datetime__item">
                            <i className="ri-time-line"></i>
                            <span>{booking.trip.time}</span>
                          </div>
                        </div>
                      </div>

                      <div className="customer__info">
                        <div className="info__item">
                          <i className="ri-user-line"></i>
                          <span>{booking.customer.fullName}</span>
                        </div>
                        <div className="info__item">
                          <i className="ri-phone-line"></i>
                          <span>{booking.customer.phone}</span>
                        </div>
                      </div>

                      <div className="pricing__info">
                        <div className="price__item">
                          <span>Base Fare</span>
                          <strong>₹{booking.cab.basePrice}</strong>
                        </div>
                        <div className="price__item">
                          <span>Per KM</span>
                          <strong>₹{booking.cab.pricePerKm}</strong>
                        </div>
                      </div>
                    </div>

                    <div className="booking__card__footer">
                      <span className="booked__at">
                        Booked on {formatDate(booking.bookedAt)}
                      </span>
                      <div className="booking__actions">
                        {booking.status === "confirmed" && (
                          <button 
                            className="action__btn cancel__btn"
                            onClick={() => cancelBooking(booking.id)}
                          >
                            <i className="ri-close-circle-line"></i> Cancel
                          </button>
                        )}
                        {booking.status === "cancelled" && (
                          <button 
                            className="action__btn delete__btn"
                            onClick={() => deleteBooking(booking.id)}
                          >
                            <i className="ri-delete-bin-line"></i> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          {bookings.length > 0 && (
            <div className="bookings__footer">
              <Link to="/cabs">
                <Button className="book__more__btn">
                  <i className="ri-add-line"></i> Book Another Cab
                </Button>
              </Link>
            </div>
          )}
        </Container>
      </section>
    </>
  );
};

export default MyCabBookings;
